import { useState, useEffect } from "react";
import {
  Heart,
  Play,
  CheckCircle,
  ArrowLeft,
  BarChart3,
  Users,
  Scale,
  MessageCircle,
  Shield,
} from "lucide-react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import OrganicShape from "../Board/OrganicShape";
import BoardChemistryAssessment from "../Board/BoardChemistryAssessment";
import BoardDynamicsOptimization from "../Board/BoardDynamicsOptimization";
import { fetchChemistryAssessments } from "../../services/businessDashApi";
import { mapBoardChemistryApiToDynamicsDisplay } from "../../lib/mapBoardChemistryAssessment";

const BoardChemistrySection = ({
  isChair = false,
  autoStartAssessment = false,
}) => {
  const [showChemistryAssessment, setShowChemistryAssessment] = useState(false);
  const [showDynamicsOptimization, setShowDynamicsOptimization] =
    useState(false);
  const [assessmentResults, setAssessmentResults] = useState(null);

  useEffect(() => {
    if (autoStartAssessment) setShowChemistryAssessment(true);
  }, [autoStartAssessment]);

  useEffect(() => {
    const loadLatestAssessment = async () => {
      try {
        const data = await fetchChemistryAssessments({
          ordering: "-created_at",
          limit: 1,
        });
        const list = Array.isArray(data)
          ? data
          : (data?.results ?? data?.data ?? []);
        if (list.length > 0) {
          const latest = list[0];
          const normalised = mapBoardChemistryApiToDynamicsDisplay(latest);
          if (normalised) {
            setAssessmentResults(normalised);
            setShowDynamicsOptimization(true);
          }
        }
      } catch {
        /* none */
      }
    };
    loadLatestAssessment();
  }, []);

  const handleChemistryComplete = (results) => {
    setAssessmentResults(results);
    setShowChemistryAssessment(false);
    setShowDynamicsOptimization(true);
  };

  if (showDynamicsOptimization && assessmentResults) {
    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          onClick={() => setShowDynamicsOptimization(false)}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Chemistry Overview
        </Button>
        <OrganicShape className="p-8 bg-gradient-subtle">
          <BoardDynamicsOptimization results={assessmentResults} />
        </OrganicShape>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Card className="overflow-hidden border-primary/20 shadow-lg">
        <div className="bg-gradient-to-br from-primary/5 via-primary/10 to-accent/5 p-8">
          <div className="flex justify-end mb-4">
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!isChair}
                    onClick={() => setShowChemistryAssessment(true)}
                  >
                    Invite / start round
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Chair-controlled cadence — opens the assessment flow</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-primary/15 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm">
              <Heart className="w-8 h-8 text-primary" />
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-2">
                  Board Chemistry Assessment
                </h2>
                <p className="text-base text-foreground/70">
                  Understand how well the board collaborates, communicates, and
                  makes decisions together.
                </p>
              </div>
              <div className="text-sm text-foreground/80 leading-relaxed space-y-3">
                <p>
                  This structured assessment helps identify alignment between
                  expectations and experiences across key areas of board
                  dynamics. Results are anonymized and aggregated to provide a
                  collective view of board effectiveness.
                </p>
                <p className="text-foreground/70">
                  Board Chemistry measures trust, collaboration, and board
                  dynamics — dimensions that change gradually and benefit from
                  periodic, not frequent, assessment.
                </p>
                <p className="font-medium text-foreground/90">
                  Recommended use: Annually, or after significant board changes
                  (Chair-controlled)
                </p>
              </div>
              <ul className="space-y-2 text-sm text-foreground/80">
                <li className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>Collaboration</span>
                </li>
                <li className="flex items-center gap-2">
                  <Scale className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>Decision-making</span>
                </li>
                <li className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>Communication</span>
                </li>
                <li className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>Trust</span>
                </li>
              </ul>
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <Badge variant="muted" className="text-xs font-normal">
                  4 categories
                </Badge>
                <Badge variant="muted" className="text-xs font-normal">
                  8 questions
                </Badge>
                <Badge variant="muted" className="text-xs font-normal">
                  ≈15 min
                </Badge>
                <Badge variant="muted" className="text-xs font-normal">
                  Full anonymity
                </Badge>
              </div>
              {assessmentResults?.completedAt && (
                <div className="flex items-center gap-2 text-sm text-primary pt-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>
                    Last completed:{" "}
                    {new Date(assessmentResults.completedAt).toLocaleDateString(
                      "en-GB",
                      {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      },
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      <div className="flex justify-center gap-6 flex-wrap">
        <Button
          size="lg"
          variant="default"
          onClick={() => setShowChemistryAssessment(true)}
          className="flex items-center gap-2 h-12 px-8 text-base !bg-[rgba(9,54,107,0.72)] !text-white hover:!bg-[rgba(9,54,107,0.88)] focus-visible:!ring-[rgba(9,54,107,0.45)]"
        >
          <Play className="w-5 h-5" />
          Start Assessment
        </Button>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                variant="outline"
                size="lg"
                disabled={!isChair || !assessmentResults}
                onClick={() => setShowDynamicsOptimization(true)}
                className="flex items-center gap-2 h-12 px-8 text-base"
              >
                <BarChart3 className="w-5 h-5" />
                Chair Summary
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {!assessmentResults
                ? "Complete an assessment first"
                : "View aggregated chair view"}
            </p>
          </TooltipContent>
        </Tooltip>
      </div>

      <BoardChemistryAssessment
        isOpen={showChemistryAssessment}
        onClose={() => setShowChemistryAssessment(false)}
        onComplete={handleChemistryComplete}
        onSave={(progress) => {
          console.log("Progress saved:", progress);
        }}
      />
    </div>
  );
};

export default BoardChemistrySection;
